import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { RefreshCw, Send, Loader2, ThumbsUp, ThumbsDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { v4 as uuidv4 } from 'uuid';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  rating?: 'good' | 'bad' | null;
}

export default function AdminTestChat() {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: "Hey! What can I help you find today?" }
  ]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string>(uuidv4());
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleReset = () => {
    setMessages([{ role: 'assistant', content: "Hey! What can I help you find today?" }]);
    setConversationId(uuidv4());
    setInput("");
  };

  const handleRating = async (messageIndex: number, rating: 'good' | 'bad') => {
    const message = messages[messageIndex];
    if (message.role !== 'assistant') return;

    // Find the previous user message
    const userMessage = messages[messageIndex - 1];
    if (!userMessage || userMessage.role !== 'user') return;

    try {
      // Save to training examples
      await supabase.from('ai_training_examples').insert({
        user_message: userMessage.content,
        ai_response: message.content,
        rating: rating,
        category: 'admin_test',
        is_active: true,
      });

      // Update message with rating
      setMessages(prev => prev.map((msg, idx) => 
        idx === messageIndex ? { ...msg, rating } : msg
      ));

      toast({
        title: rating === 'good' ? "👍 Marked as good" : "👎 Marked as bad",
        description: "Training example saved for future improvements",
      });
    } catch (error) {
      console.error('Failed to save rating:', error);
      toast({
        title: "Error",
        description: "Failed to save training example",
        variant: "destructive",
      });
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage = input.trim();
    setInput("");
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsStreaming(true);

    try {
      const { data, error } = await supabase.functions.invoke('ask-circle-chat', {
        body: { 
          conversationId,
          messages: [...messages, { role: 'user', content: userMessage }],
          isAdminTest: true
        }
      });

      if (error) throw error;

      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error: any) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${error.message}` }]);
    } finally {
      setIsStreaming(false);
    }
  };

  return (
    <Card className="h-[600px] flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold">✨ Test Your Bot</h3>
        <Button onClick={handleReset} variant="ghost" size="sm">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
            <div className={`max-w-[80%] rounded-lg p-3 ${
              msg.role === 'user' 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-muted'
            }`}>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
            {msg.role === 'assistant' && idx > 0 && (
              <div className="flex gap-1 mt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 px-2 ${msg.rating === 'good' ? 'bg-green-100 text-green-700' : ''}`}
                  onClick={() => handleRating(idx, 'good')}
                >
                  <ThumbsUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 px-2 ${msg.rating === 'bad' ? 'bg-red-100 text-red-700' : ''}`}
                  onClick={() => handleRating(idx, 'bad')}
                >
                  <ThumbsDown className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        ))}
        {isStreaming && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg p-3">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
      </div>

      <div className="p-4 border-t">
        <div className="flex gap-2">
          <Input 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Send a message..."
            disabled={isStreaming}
          />
          <Button onClick={handleSend} disabled={isStreaming || !input.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
